import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileText,
  Gauge,
  Lightbulb,
  LogOut,
  Menu,
  Radio,
  ScrollText,
  Settings,
  ShieldCheck,
  Upload,
  Users,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { useLiveUpdates } from "../live/useLiveUpdates";
import { NavLink, useRouter } from "../routing/Router";
import type { Role } from "../types";

interface NavItem {
  to: string;
  label: string;
  icon: typeof Gauge;
  roles: Role[];
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Overview", icon: Gauge, roles: ["owner", "manager"] },
  {
    to: "/work",
    label: "Work",
    icon: ClipboardList,
    roles: ["owner", "ideation", "manager", "uploader"],
  },
  { to: "/ideas", label: "Ideas", icon: Lightbulb, roles: ["owner", "ideation", "manager"] },
  { to: "/profiles", label: "Profiles", icon: FileText, roles: ["owner", "manager"] },
  { to: "/uploads", label: "Uploads", icon: Upload, roles: ["owner", "uploader"] },
  { to: "/workers", label: "Workers", icon: Activity, roles: ["owner"] },
  { to: "/accounts", label: "Accounts", icon: Users, roles: ["owner"] },
  { to: "/system", label: "System", icon: Settings, roles: ["owner"] },
  { to: "/audit", label: "Audit", icon: ScrollText, roles: ["owner"] },
  { to: "/security", label: "Security", icon: ShieldCheck, roles: ["owner"] },
];

export function RoleNavigation({
  role,
  onNavigate,
}: {
  role: Role;
  onNavigate?: () => void;
}) {
  return (
    <nav className="primary-nav" aria-label="Primary navigation">
      {NAV_ITEMS.filter((item) => item.roles.includes(role)).map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            onClick={onNavigate}
            className={(isActive) => (isActive ? "active" : undefined)}
          >
            <Icon size={18} aria-hidden="true" />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { session, logout, refresh } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const { path, navigate } = useRouter();

  useEffect(() => setMobileOpen(false), [path]);
  useEffect(() => {
    const expired = () => setSessionExpired(true);
    window.addEventListener("swipecut:session-expired", expired);
    return () => window.removeEventListener("swipecut:session-expired", expired);
  }, []);

  const onEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent("swipecut:refresh"));
  }, []);
  const onPoll = useCallback(async () => {
    await refresh();
    window.dispatchEvent(new CustomEvent("swipecut:refresh"));
  }, [refresh]);

  const eventSourceFactory = useMemo(
    () => (url: string) => new EventSource(url, { withCredentials: true }),
    [],
  );
  const liveState = useLiveUpdates({
    enabled: Boolean(session),
    onEvent,
    onPoll,
    eventSourceFactory,
  });

  if (!session) return null;

  const isLive = liveState === "live";

  return (
    <div className={`app-layout ${collapsed ? "sidebar-collapsed" : ""}`}>
      <header className="mobile-topbar">
        <button
          className="icon-button"
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open navigation"
        >
          <Menu size={20} />
        </button>
        <Brand />
        <LiveIndicator live={isLive} state={liveState} compact />
      </header>

      {mobileOpen ? (
        <button
          className="mobile-scrim"
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      <aside className={`sidebar ${mobileOpen ? "mobile-open" : ""}`}>
        <div className="sidebar-brand">
          <Brand />
          <button
            className="icon-button mobile-only"
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          >
            <X size={18} />
          </button>
        </div>
        <RoleNavigation role={session.user.role} onNavigate={() => setMobileOpen(false)} />
        <div className="sidebar-foot">
          <LiveIndicator live={isLive} state={liveState} />
          <div className="signed-in-user">
            <span className="avatar" aria-hidden="true">
              {session.user.displayName.slice(0, 1).toUpperCase()}
            </span>
            <div>
              <strong>{session.user.displayName}</strong>
              <span>{session.user.role}</span>
            </div>
            <button
              className="icon-button"
              type="button"
              title="Sign out"
              aria-label="Sign out"
              onClick={() => void logout().then(() => navigate("/login"))}
            >
              <LogOut size={17} />
            </button>
          </div>
        </div>
        <button
          className="collapse-button"
          type="button"
          title={collapsed ? "Expand navigation" : "Collapse navigation"}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          onClick={() => setCollapsed((value) => !value)}
        >
          {collapsed ? <ChevronRight size={17} /> : <ChevronLeft size={17} />}
        </button>
      </aside>

      <main className="main-content">
        <div className="desktop-topbar">
          <div className="environment-label">Internal operations</div>
        </div>
        <div className="page-content">{children}</div>
      </main>

      {sessionExpired ? (
        <div className="session-expired" role="alertdialog" aria-modal="true">
          <section>
            <ShieldCheck size={24} />
            <h2>Session expired</h2>
            <p>Sign in again to continue. Unsaved form text will remain in this browser.</p>
            <button
              className="button primary"
              type="button"
              onClick={() => {
                setSessionExpired(false);
                void logout().finally(() => navigate("/login"));
              }}
            >
              Return to sign in
            </button>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function Brand() {
  return (
    <div className="brand">
      <span className="brand-mark">
        <Radio size={17} aria-hidden="true" />
      </span>
      <span>
        <strong>SwipeCut</strong>
        <small>2.0 Internal</small>
      </span>
    </div>
  );
}

function LiveIndicator({
  live,
  state,
  compact = false,
}: {
  live: boolean;
  state: string;
  compact?: boolean;
}) {
  const Icon = live ? Wifi : WifiOff;
  const label = live ? "Live" : state === "polling" ? "Reconnecting" : "Connecting";
  return (
    <div className={`live-indicator ${live ? "is-live" : ""} ${compact ? "compact" : ""}`}>
      <Icon size={15} aria-hidden="true" />
      {!compact ? <span>{label}</span> : null}
    </div>
  );
}
